import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { invalidateInventory } from '../lib/hooks';
import { Alert, Button, Modal, Table, Td, Th } from './ui';

interface ImportResult {
  created: number;
  updated: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

const TEMPLATE_HEADERS = [
  'Asset Tag',
  'Name',
  'Category',
  'Status',
  'Condition',
  'Manufacturer',
  'Model',
  'Serial Number',
  'Building',
  'Room',
  'Purchase Date',
  'Purchase Price',
  'Vendor',
  'PO Number',
  'Funding Source',
  'Warranty Expires',
  'Notes',
];

function downloadTemplate() {
  const example = ['', 'Dell Latitude 5450', 'Laptop', 'Available', 'Good', 'Dell', 'Latitude 5450', 'ABC1234', 'Upper School', 'Room 101', '2025-07-15', '1149.00', 'CDW-G', 'PO-2025-1001', 'Operating Budget', '2028-07-15', ''];
  const blob = new Blob([`${TEMPLATE_HEADERS.join(',')}\r\n${example.join(',')}\r\n`], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'edvora-asset-import-template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

export function ImportAssetsModal({ open, onClose }: { open: boolean; onClose(): void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setFile(null);
      setResult(null);
      setError('');
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: async () => api.post<ImportResult>('/assets/import', { csv: await file!.text() }),
    onSuccess: (data) => {
      setResult(data);
      invalidateInventory(queryClient);
    },
    onError: (err) => setError(errorMessage(err)),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Import assets from CSV"
      description="Rows with an existing asset tag update that asset; other rows create new assets."
      footer={
        result ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" icon={<Upload />} disabled={!file} loading={mutation.isPending} onClick={() => mutation.mutate()}>
              Import
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3 text-center">
            {[
              ['Created', result.created, 'text-emerald-700'],
              ['Updated', result.updated, 'text-sky-700'],
              ['Skipped', result.skipped, result.skipped ? 'text-red-700' : 'text-slate-700'],
            ].map(([label, value, color]) => (
              <div key={label as string} className="rounded-lg border border-slate-200 p-3">
                <p className={`text-2xl font-semibold ${color}`}>{value as number}</p>
                <p className="text-xs text-slate-500">{label as string}</p>
              </div>
            ))}
          </div>
          {result.errors.length > 0 && (
            <div className="rounded-lg border border-slate-200">
              <Table>
                <thead>
                  <tr>
                    <Th>Row</Th>
                    <Th>Problem</Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.errors.map((e) => (
                    <tr key={e.row}>
                      <Td className="w-16 font-mono text-xs">{e.row}</Td>
                      <Td className="text-red-700">{e.message}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {error && <Alert tone="red">{error}</Alert>}
          <label className="flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-slate-300 px-6 py-10 text-center hover:border-brand-400 hover:bg-brand-50/30">
            <FileSpreadsheet className="size-8 text-slate-400" />
            <span className="mt-2 text-sm font-medium text-slate-800">{file ? file.name : 'Choose a CSV file'}</span>
            <span className="mt-1 text-xs text-slate-500">Up to 5,000 rows. Categories, buildings, and rooms are created if they don’t exist.</span>
            <input type="file" accept=".csv,text/csv" className="sr-only" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </label>
          <p className="text-sm text-slate-600">
            Recognized columns: {TEMPLATE_HEADERS.join(', ')}.{' '}
            <button type="button" onClick={downloadTemplate} className="font-medium text-brand-700 hover:underline">
              Download a template
            </button>
          </p>
        </div>
      )}
    </Modal>
  );
}
